import { Button } from "@/shadcnComponents/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/shadcnComponents/ui/card";
import { Separator } from "@/shadcnComponents/ui/separator";
import { ArrowRightIcon, ChevronRight } from "lucide-react";
import { CheckoutItem } from "../../CampaignInterface/NewCampaignInterface";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shadcnComponents/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/shadcnComponents/ui/radio-group";
import mixpanel from "mixpanel-browser";
import { Label } from "@/shadcnComponents/ui/label";
import { useState } from "react";

export const LiveCampaignCheckoutCards = ({
  selectedCreatorsLength,
  Amount,
  OnClickNext,
  isPaid,
  AlreadyPaidAmount,
  discountedPrice,
  additionalDiscounts,
  collaborationCharges,
  campaignCharges,
  usedCollaborationCredits,
  usedAnchorsCredits,
  usedCampaignAddOn,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
}: {
  selectedCreatorsLength: number;
  Amount: number;
  OnClickNext: () => void;
  isPaid: boolean;
  AlreadyPaidAmount: number;
  discountedPrice: number;
  additionalDiscounts?: {
    name: string;
    amount: number;
  }[];
  collaborationCharges: number;
  campaignCharges: number;
  usedCollaborationCredits: number;
  usedAnchorsCredits: number;
  usedCampaignAddOn: number;
  setSelectedPaymentMethod: React.Dispatch<React.SetStateAction<string>>;
  selectedPaymentMethod: "card" | "upi";
}) => {
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [finalAmount, setFinalAmount] = useState(0);

  const additionalAmounts =
    collaborationCharges * selectedCreatorsLength +
    usedCampaignAddOn * campaignCharges -
    (usedCollaborationCredits * collaborationCharges + usedAnchorsCredits);
  const gstAmount = (
    (Amount + additionalAmounts - discountedPrice) *
    0.18
  )?.toFixed(2);
  const totalAmount = (
    Amount +
    additionalAmounts -
    discountedPrice +
    (Amount + additionalAmounts - discountedPrice) * 0.18
  )?.toFixed(2);

  let totalAdditionalDiscounts = 0;
  additionalDiscounts?.forEach((item) => {
    totalAdditionalDiscounts = item?.amount;
  });

  const BalanceAmount =
    parseFloat(totalAmount) -
    parseFloat(AlreadyPaidAmount?.toFixed(2)) -
    parseFloat(totalAdditionalDiscounts?.toFixed(2));

  const checkoutItems: CheckoutItem[] = [
    { label: "Campaign Amount", amount: Amount },
    {
      label: "Collaboration Charges",
      amount: selectedCreatorsLength * collaborationCharges,
    },
  ];

  if (usedCampaignAddOn > 0) {
    checkoutItems.push({
      label: "Campaign charges",
      amount: campaignCharges,
    });
  }

  if (discountedPrice > 0) {
    checkoutItems.push({
      label: "Discounted Price",
      amount: -discountedPrice?.toFixed(2),
    });
  }

  if (additionalDiscounts?.length > 0) {
    additionalDiscounts?.map((item) => {
      checkoutItems.push({
        label: item.name,
        amount: -item.amount,
      });
    });
  }

  if (usedAnchorsCredits > 0) {
    checkoutItems.push({
      label: "Anchors Credits applied",
      amount: -usedAnchorsCredits,
    });
  }

  if (usedCollaborationCredits > 0) {
    checkoutItems.push({
      label: "Collaboration Credits applied",
      amount: -(usedCollaborationCredits * collaborationCharges),
    });
  }

  const handlePaymentMethodChange = (value: "card" | "upi") => {
    setSelectedPaymentMethod(value);
    if (value === "card") {
      setFinalAmount(BalanceAmount * 1.023); // Adding 2.3% for card payments, including GST
    } else {
      setFinalAmount(BalanceAmount); // Include GST for UPI as well
    }
  };

  return (
    <>
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <h1 className="text-2xl font-bold mb-6 text-center">Checkout</h1>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-6">
            {/* campaign details */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Campaign Details</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Campaign Amount</span>
                  <span className="font-medium">
                    ₹
                    {Amount
                      ? new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(Amount)
                      : 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Number of Influencers</span>
                  <span className="font-medium">{selectedCreatorsLength}</span>
                </div>
                <div className="flex justify-between">
                  <span>Collaborations Count</span>
                  <span className="font-medium">{selectedCreatorsLength}</span>
                </div>
                <div className="flex justify-between">
                  <span>Collaboration Charges (per unit)</span>
                  <span className="font-medium">
                    ₹{collaborationCharges?.toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {/* order summary */}
            <Card className="shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {checkoutItems.map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <span className={item.amount < 0 ? "text-green-600" : ""}>
                      {item.label}
                    </span>
                    <span
                      className={
                        item.amount < 0 ? "text-green-600" : "font-medium"
                      }
                    >
                      ₹{Math.abs(item.amount).toFixed(2)}
                    </span>
                  </div>
                ))}
                <Separator />
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-medium">
                    ₹
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(Amount + additionalAmounts - discountedPrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>GST (18%)</span>
                  <span className="font-medium">
                    ₹
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(gstAmount))}
                  </span>
                </div>
                {AlreadyPaidAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Partially paid</span>
                    <span className="font-medium">
                      ₹
                      {new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(AlreadyPaidAmount)}
                    </span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-base font-bold">
                  <span className={`${isPaid ? "text-green-500 " : ""}`}>
                    {isPaid ? "Paid" : "Balance"}
                  </span>
                  <span className={`${isPaid ? "text-green-500 " : ""}`}>
                    ₹
                    {BalanceAmount
                      ? new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(BalanceAmount)
                      : 0}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* payment options */}
            {isPaid ? (
              <Button
                onClick={OnClickNext}
                variant="destructive"
                className="flex w-full gap-[10px] items-center "
              >
                {" "}
                <span>Set Live Dates</span> <ArrowRightIcon size={16} />{" "}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  mixpanel.track(
                    "Brand Live Campaign - CheckOut Section: Continue to Pay- clicked"
                  );
                  setSelectedPaymentMethod(null);
                  setShowPaymentPopup(true);

                  // OnClickNext();
                }}
                className="w-full flex items-center gap-2"
                variant="destructive"
              >
                {"Continue to Pay"}
                <ChevronRight size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showPaymentPopup} onOpenChange={setShowPaymentPopup}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Select Payment Method</DialogTitle>
            <DialogDescription>
              Choose your preferred payment method to complete the transaction.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <RadioGroup
              onValueChange={handlePaymentMethodChange}
              className="space-y-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="card" id="card" />
                <Label htmlFor="card">
                  Credit Card, Debit Card, Net Banking, Wallet (2.3% extra fee,
                  incl. GST)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="upi" id="upi" />
                <Label htmlFor="upi">UPI (No extra fee)</Label>
              </div>
            </RadioGroup>
          </div>
          <DialogFooter>
            {selectedPaymentMethod && (
              <Button
                onClick={() => {
                  mixpanel.track(
                    "Brand Live Campaign - CheckOut Section: Pay - clicked"
                  );
                  setShowPaymentPopup(false);
                  OnClickNext();
                }}
                className="w-full"
              >
                Pay ₹{finalAmount?.toFixed(2)} (incl. GST) via{" "}
                {selectedPaymentMethod === "card" ? "Card/Banking" : "UPI"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
